import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchCollectionWithPost, useFetchResource } from '@app/src/api/fetchHooks'
import { useDeleteResource } from '@app/src/api/updateHooks'
import Permissions from '@app/src/auth/permissions'
import type { ActionButton } from '@app/src/components/ActionButtons'
import type { AiAction } from '@app/src/components/AiActionsDropdown'
import AiActionsDropdown from '@app/src/components/AiActionsDropdown'
import { useDrawer } from '@app/src/components/Drawer/DrawerContext'
import DrawerViewAddProvider from '@app/src/components/Drawer/Views/DrawerViewAddProvider'
import DrawerViewExport from '@app/src/components/Drawer/Views/DrawerViewExport'
import DrawerViewImportInitialStep from '@app/src/components/Drawer/Views/DrawerViewImportInitialStep'
import { useDrawer_DEPRECATED } from '@app/src/components/Drawer_DEPRECATED'
import type { ProviderUpdateType } from '@app/src/components/ManageProviderDrawer/CategoryDrawer/CategoryDrawerContent'
import UpdateProvidersDrawer from '@app/src/components/ManageProviderDrawer/UpdateProvidersDrawer'
import Table from '@app/src/components/Table'
import { useAccount } from '@app/src/context/AccountContext'
import { useAuthentication } from '@app/src/context/AuthenticationContext'
import { ProvidersModalPurpose, useProvidersModal } from '@app/src/context/ProvidersModalContext'
import { getProviderExportColumns } from '@app/src/export-columns/provider'
import useCurrentProviderType from '@app/src/hooks/currentProviderType'
import useErrorNotification from '@app/src/hooks/errorNotification'
import { useDialogState } from '@app/src/hooks/mui-hooks'
import usePaginationWithQuery from '@app/src/hooks/paginationWithQuery'
import useSort from '@app/src/hooks/sorting'
import { useBulkCreateExpectation } from '@app/src/hooks/useBulkCreateExpectation'
import useProviderSpendInformation from '@app/src/hooks/useProviderSpendInformation'
import { useSelectableRowsWithPostSelectAll } from '@app/src/hooks/useSelectableRows/useSelectableRowsWithPostSelectAll'
import { useWriteAccess } from '@app/src/hooks/useWriteAccess'
import ProviderHeader from '@app/src/pages/ResourceCollection/Collections/Provider/ProviderHeader'
import ProviderRow from '@app/src/pages/ResourceCollection/Collections/Provider/ProviderRow'
import ProvidersFilters from '@app/src/pages/ResourceCollection/Filters/ProvidersFilters'
import type { FilterGroup } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import type { Category } from '@app/src/types/categories'
import type { Provider } from '@app/src/types/organizations'
import { ProviderLinkStatus } from '@app/src/types/organizations'
import { insertIf } from '@app/src/utils/helpersTs'
import { ResourceTypes } from '@app/src/wf-constants'
import {
  DeleteOutlineOutlined,
  LabelOutlined,
  MailOutlined,
  NotificationsActiveOutlined,
  PublishedWithChangesOutlined,
  Warning,
} from '@mui/icons-material'
import { Box, Stack } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { capitalize } from 'lodash'
import { useConfirm } from 'material-ui-confirm'
import type { FC } from 'react'
import React, { useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { useLocation } from 'react-router'
import type { ResourceCollectionSceneProps } from '../../ResourceCollectionScene'
import ResourceCollectionScene from '../../ResourceCollectionScene'
import TabularImportProvidersModal from '@app/src/pages/TabularImport/providers/TabularImportProvidersModal'
import ImportProvidersModal from '../Provider/ConnectProviders/ImportProvidersModal'
import type { ProviderTabs } from './ProvidersScene'
import { useProviderApiQueryFilters } from '@app/src/hooks/useProviderApiQueryFilters'
import { useProviderOverallRisks } from '@app/src/hooks/useProviderOverallRisks'
import useAiScreeningEnabled from '@app/src/hooks/useAiScreeningEnabled'

type BaseProviderSceneProps = {
  implicitFilters?: FilterGroup[]
  allowedFilters: string[]
  isConnectedProvidersPage?: boolean
  resourceView: ProviderTabs
  banner?: React.ReactNode
} & Omit<ResourceCollectionSceneProps, 'title'>

const BaseProviderScene: FC<BaseProviderSceneProps> = ({
  implicitFilters = [],
  allowedFilters,
  isConnectedProvidersPage,
  ...props
}) => {
  const { hasPermission } = useAccount()
  const { formatMessage } = useIntl()
  const { solution } = useAuthentication().scope
  const { openDrawer } = useDrawer()
  const hasWriteAccess = useWriteAccess()
  const location = useLocation<{
    fromDashboardCategorizeButton: boolean
    selectedCompany: ProviderUpdateType
    openAddProviderDrawer: boolean
  }>()
  const { sorting, toggleSorting } = useSort()
  const userFilters = useProviderApiQueryFilters(allowedFilters)
  const [page, pageSize, setPage, setPageSize, resetPage] = usePaginationWithQuery({ page: 1, pageSize: 50 }, [
    userFilters,
  ])
  const resourceType = useCurrentProviderType()
  const { handleOpenConnectDialog } = useProvidersModal()
  const [isImportDialogOpen, openImportDialog, closeImportDialog] = useDialogState(false)
  const [isBetaImportDialogOpen, openBetaImportDialog, closeBetaImportDialog] = useDialogState(false)
  const [isUpdateDrawerOpen, openUpdateDrawer, closeUpdateDrawer] = useDrawer_DEPRECATED(
    location?.state?.fromDashboardCategorizeButton,
  )
  const { showAdditionalExportColumns, topicalRisk = false } = useFlags()
  const isAiScreeningEnabled = useAiScreeningEnabled()

  const confirm = useConfirm()
  const queryClient = useQueryClient()
  const { showErrorNotification } = useErrorNotification()

  const rawExportPayload = {
    filter: implicitFilters,
    sort: sorting,
    include: [
      'country',
      'creatorUser',
      'creatorOrganization',
      'categoryOptions.category',
      'referralContact',
      'referralContact.referralEmailHistory',
      'organization.contacts',
      'organization.contacts.user',
      'organization.industryNaceCode',
      'organization.subIndustryNaceCode',
      'organization.companySizeRange',
      'organization.revenueRange',
      'organization.registrationNumber',
      'organization.country',
      'organization.image',
      'organization.locations',
      'spends',
      'providerOwners.user',
      'enrichments.industry',
      'enrichments.subIndustry',
      'industry',
      'subIndustry',
      'assessments',
    ],
  }

  const payload = {
    ...rawExportPayload,
    filter: [...rawExportPayload.filter, ...userFilters],
    pagination: {
      pageNumber: page,
      itemsPerPage: pageSize,
    },
  }

  const {
    items: pageProviders,
    count,
    isFetching,
    isError,
  } = useFetchCollectionWithPost<Provider>({
    key: FetchKey.ProviderCollection,
    endpoint: endpoints.providersCollection,
    payload,
  })

  const {
    allRows,
    selectedRowsIds,
    setSelectedRowsIds,
    isLoadingAllRows,
    isAllSelected,
    handleSelectAll,
    handleCheckboxChange,
    isHeaderChecked,
    handleHeaderCheckboxChange,
  } = useSelectableRowsWithPostSelectAll<Provider>({
    initialState: [location?.state?.selectedCompany?.providerId ?? []].flat(),
    rowsInPage: pageProviders,
    basePayload: payload,
    fetchKey: FetchKey.ProviderCollection,
    endpoint: endpoints.providersCollection,
    count,
  })

  // Behind the same flag as the risk tab the chip links to (see hooks/relatedTabs). No ids leaves the
  // query disabled, and ProviderRow/ProviderHeader drop the column on the same flag.
  const { overallRiskByProviderId, isLoading: isLoadingOverallRisks } = useProviderOverallRisks(
    topicalRisk ? pageProviders.map(provider => provider.id) : [],
  )

  const { organizationCurrency, isCurrencyLoading, spendPeriodFocus } = useProviderSpendInformation()

  const { data: categories } = useFetchResource<Category[]>({
    key: FetchKey.Category,
    endpoint: endpoints.category,
  })

  const { mutateAsync: deleteProvidersAsync, isPending: isDeletingProviders } = useDeleteResource()

  const providers = allRows.length ? allRows : pageProviders

  const selectedProviders = useMemo(
    () => providers?.filter(provider => selectedRowsIds.includes(provider.id)) ?? [],
    [selectedRowsIds.length, providers.length, page, isFetching],
  )

  const selectedProvidersByLinkStatus = useMemo(() => {
    return Object.groupBy(selectedProviders, p => p.linkStatus)
  }, [selectedProviders])

  const {
    isEnabled: showBaselineExpectation,
    open: openBulkCreateExpectation,
    modal: bulkExpectationModal,
  } = useBulkCreateExpectation(selectedProviders)

  useEffect(() => {
    setSelectedRowsIds(prev => providers.filter(provider => prev.includes(provider.id)).map(provider => provider.id))
  }, [providers])

  useEffect(() => {
    if (location.state?.openAddProviderDrawer) {
      openDrawer(<DrawerViewAddProvider providerType={resourceType} />)
    }
  }, [location.state?.openAddProviderDrawer, openDrawer, resourceType])

  const isFetchingProviders = isFetching || isLoadingAllRows

  const aiActions = useMemo<AiAction[]>(() => {
    const actions: AiAction[] = [
      {
        labelId: 'resourceCollections.general.enrichProperties',
        onClick: () => handleOpenConnectDialog(selectedProviders, ProvidersModalPurpose.ScheduleEnrichment),
        disabled: isFetchingProviders,
      },
    ]
    if (isAiScreeningEnabled) {
      actions.push({
        labelId: 'resourceCollections.general.screenRequests',
        onClick: () => handleOpenConnectDialog(selectedProviders, ProvidersModalPurpose.ScheduleScreening),
        disabled: isFetchingProviders,
      })
    }
    return actions
  }, [isAiScreeningEnabled, selectedProviders, isFetchingProviders, handleOpenConnectDialog])

  const deleteProvider = async () => {
    await deleteProvidersAsync(
      { url: endpoints.bulkDeleteProviders(selectedRowsIds) },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: [FetchKey.ProviderCollection] })
          queryClient.resetQueries({ queryKey: [FetchKey.ProviderCollection] })
          queryClient.invalidateQueries({ queryKey: [FetchKey.Provider] })
          queryClient.invalidateQueries({ queryKey: [FetchKey.ProviderCount] })
          queryClient.invalidateQueries({ queryKey: [FetchKey.ProviderFacets] })
        },
        onError: error => {
          showErrorNotification({ requestError: error })
        },
      },
    )
  }

  const secondConfirmationWhenActiveReferralLinks = () => {
    confirm({
      title: (
        <Stack direction="row" spacing={2}>
          <Warning />
          <Box>{formatMessage({ id: 'general.areYouSure' })}</Box>
        </Stack>
      ),

      description: formatMessage({ id: 'resourceExplorer.provider.bulkDelete.deleteWhenReferralContact' }),
      confirmationButtonProps: { variant: 'contained', startIcon: <DeleteOutlineOutlined />, color: 'error' },
      confirmationText: formatMessage({ id: 'general.deleteAnyway' }),
      buttonOrder: ['confirm', 'cancel'],
    }).then(deleteProvider)
  }

  const confirmDelete = () => {
    confirm({
      title: formatMessage({ id: 'resourceExplorer.provider.bulkDelete.header' }, { count: selectedRowsIds.length }),
      description: formatMessage({
        id:
          selectedRowsIds.length === 1
            ? 'resourceExplorer.provider.delete.description'
            : 'resourceExplorer.provider.bulkDelete.description',
      }),
      confirmationText: (
        <>
          <DeleteOutlineOutlined />
          {formatMessage({ id: 'general.delete' })}
        </>
      ),
      confirmationButtonProps: { style: { backgroundColor: 'white', color: 'black' } },
      cancellationButtonProps: { style: { backgroundColor: 'black', color: 'white' } },
      buttonOrder: ['confirm', 'cancel'],
    }).then(async () => {
      const someProvidersHaveLinks = Boolean(selectedProvidersByLinkStatus[ProviderLinkStatus.Invited]?.length)
      if (someProvidersHaveLinks) {
        secondConfirmationWhenActiveReferralLinks()
      } else {
        await deleteProvider()
      }
    })
  }

  const handleExport = (selectedIds?: number[], showAdditionalExportColumns?: boolean) => {
    openDrawer(
      <DrawerViewExport
        resourceType={ResourceTypes.Provider}
        count={count}
        userFilter={userFilters}
        exportColumns={getProviderExportColumns(
          solution,
          undefined,
          undefined,
          showAdditionalExportColumns,
          spendPeriodFocus,
        )}
        rawExportPayload={rawExportPayload}
        selectedIds={selectedIds}
        resourceView={props.resourceView}
      />,
    )
  }

  return (
    <>
      <ResourceCollectionScene
        title={formatMessage({ id: 'resourceTypes.provider' })}
        actionButtons={[
          {
            label: formatMessage({ id: 'resourceCollections.general.export' }),
            variant: 'outlined',
            onClick: () => handleExport(selectedRowsIds, showAdditionalExportColumns),
            disabled: isFetchingProviders || !count,
          },
          ...insertIf<ActionButton>(hasWriteAccess, {
            label: formatMessage({ id: 'resourceCollections.general.import' }),
            variant: 'outlined',
            onClick: () =>
              openDrawer(
                <DrawerViewImportInitialStep
                  openImportProviderModal={openImportDialog}
                  openBetaImportProviderModal={openBetaImportDialog}
                  allowedFilters={allowedFilters}
                  providerType={resourceType}
                  solution={solution}
                  count={count}
                />,
              ),
            disabled: isFetchingProviders || hasPermission(Permissions.TRANSPARENCY_USER),
          }),
          ...insertIf<ActionButton>(hasWriteAccess, {
            label: formatMessage(
              { id: 'resourceCollections.create.addProvider' },
              {
                provider: formatMessage(
                  {
                    id: `general.${capitalize(resourceType)}`,
                  },
                  { count: 1 },
                ).toLowerCase(),
              },
            ),
            variant: 'contained',
            onClick: () => openDrawer(<DrawerViewAddProvider providerType={resourceType} />),
            disabled: isFetchingProviders,
          }),
        ]}
        filter={
          <Box sx={{ pb: 0 }}>
            <ProvidersFilters allowedFilters={allowedFilters} implicitFilters={implicitFilters} resetPage={resetPage} />
          </Box>
        }
        buttonRowPrefix={
          selectedRowsIds.length && hasWriteAccess ? (
            <AiActionsDropdown actions={aiActions} disabled={isFetchingProviders} />
          ) : undefined
        }
        buttonRow={
          selectedRowsIds.length
            ? [
                ...insertIf<ActionButton>(
                  hasWriteAccess && Boolean(selectedProvidersByLinkStatus[ProviderLinkStatus.Created]),
                  {
                    label: formatMessage(
                      { id: 'resourceCollections.general.connect' },
                      { count: selectedProvidersByLinkStatus[ProviderLinkStatus.Created]?.length ?? 0 },
                    ),
                    startIcon: <MailOutlined />,
                    onClick: () =>
                      handleOpenConnectDialog(
                        selectedProvidersByLinkStatus[ProviderLinkStatus.Created] ?? [],
                        ProvidersModalPurpose.InitialInvitation,
                      ),
                    disabled: isFetchingProviders,
                  },
                ),
                ...insertIf<ActionButton>(
                  hasWriteAccess && Boolean(selectedProvidersByLinkStatus[ProviderLinkStatus.Invited]),
                  {
                    label: formatMessage(
                      { id: 'resourceCollections.general.sendReminder' },
                      { count: selectedProvidersByLinkStatus[ProviderLinkStatus.Invited]?.length ?? 0 },
                    ),
                    startIcon: <NotificationsActiveOutlined />,
                    onClick: () =>
                      handleOpenConnectDialog(
                        selectedProvidersByLinkStatus[ProviderLinkStatus.Invited] ?? [],
                        ProvidersModalPurpose.ExtraReminder,
                      ),
                    disabled: isFetchingProviders,
                  },
                ),
                ...insertIf<ActionButton>(hasWriteAccess, {
                  label: formatMessage(
                    { id: 'resourceCollections.general.categorize' },
                    { count: selectedRowsIds.length },
                  ),
                  startIcon: <LabelOutlined />,
                  onClick: openUpdateDrawer,
                  disabled: isFetchingProviders,
                }),
                ...insertIf<ActionButton>(hasWriteAccess && showBaselineExpectation, {
                  label: formatMessage(
                    { id: 'bulkCreateExpectation.entryButton' },
                    { count: selectedProviders?.length ?? 0 },
                  ),
                  startIcon: <PublishedWithChangesOutlined />,
                  onClick: openBulkCreateExpectation,
                  disabled: isFetchingProviders,
                }),
                ...insertIf<ActionButton>(hasWriteAccess, {
                  label: formatMessage(
                    { id: 'resourceCollections.general.deleteWithCount' },
                    { count: selectedRowsIds.length },
                  ),
                  onClick: confirmDelete,
                  startIcon: <DeleteOutlineOutlined />,
                  disabled: isFetchingProviders,
                  loading: isDeletingProviders,
                }),
                {
                  label: formatMessage(
                    { id: 'resourceCollections.general.exportWithCount' },
                    { count: selectedRowsIds.length },
                  ),
                  onClick: () => handleExport(selectedRowsIds),
                  disabled: isFetchingProviders || !count,
                },
                ...insertIf<ActionButton>(count > pageSize, {
                  label: formatMessage({ id: 'resourceCollections.general.selectAll' }, { count }),
                  variant: 'text',
                  onClick: handleSelectAll,
                  disabled: isFetching || isAllSelected(),
                  loading: isLoadingAllRows,
                }),
              ]
            : undefined
        }
        {...props}
      >
        {props.banner}
        <Table<Provider>
          RowComponent={({ row }) => (
            <ProviderRow
              row={row}
              categories={categories}
              spendPeriodFocus={spendPeriodFocus}
              onCheckboxChange={handleCheckboxChange}
              selectedProviderIds={selectedRowsIds}
              organizationCurrency={organizationCurrency}
              overallRiskStatus={overallRiskByProviderId.get(row.id)}
              isLoadingOverallRisk={isLoadingOverallRisks}
            />
          )}
          HeaderComponent={() => (
            <ProviderHeader
              categories={categories}
              spendPeriodFocus={spendPeriodFocus}
              toggleSorting={toggleSorting}
              activeSorting={sorting}
              isHeaderChecked={isHeaderChecked}
              handleHeaderCheckboxChange={handleHeaderCheckboxChange}
            />
          )}
          data={pageProviders}
          isLoading={isFetching || isCurrencyLoading}
          count={count}
          isError={isError}
          page={page}
          pageSize={pageSize}
          setPage={setPage}
          setPageSize={setPageSize}
          stickyColumnIndex={2}
        />
      </ResourceCollectionScene>
      <UpdateProvidersDrawer
        selectedProviders={providers
          .filter(provider => selectedRowsIds.includes(provider.id))
          .map(provider => ({
            providerId: provider.id,
            name: provider.name,
            activityStatus: provider.activityStatus,
            finalRiskRating: provider.finalRiskRating,
            priority: provider.priority,
            providerApprovalStatus: provider.providerApprovalStatus,
            categoryOptionIds: provider.categoryOptions?.map(catOpt => catOpt.id),
            ownerUserIds: provider.ownerUserIds,
            tier: provider.tier,
            supplierUsage: provider.supplierUsage,
          }))}
        setSelectedProviders={setSelectedRowsIds}
        isDrawerOpen={isUpdateDrawerOpen}
        closeDrawer={closeUpdateDrawer}
      />
      <TabularImportProvidersModal onClose={closeBetaImportDialog} open={isBetaImportDialogOpen} />
      <ImportProvidersModal onClose={closeImportDialog} open={isImportDialogOpen} />
      {bulkExpectationModal}
    </>
  )
}

export default BaseProviderScene
